import {WorksitesFiltersProps, WorksitesFiltersType} from "./types";
import {ADVANCED_CONTROL_ROLES} from "@feature/worksites/home/filters/lib/consts";
import AuthenticationService from "@services/AuthenticationService";
import TranslatedKey from "@components/TranslatedKey";
import Toggle from "@ui-components/Toggle";
import Input from "@ui-components/Input";
import SearchSelect from "@ui-components/SearchSelect";
import React from "react";
import {useIntl} from "react-intl";
import {useHomeProvinces} from "@feature/worksites/home/hooks/useHomeProvinces";
import {WORKSITE_STATUS_CHOICES} from "@utils/constants";
import {UsersMinimalSchema} from "@feature/types";

export const WorksitesFilters = ({ value, onChange, users, worksitesTypes }: WorksitesFiltersProps) => {

  // hooks
  const intl = useIntl();
  const {data: provinces} = useHomeProvinces();

  // utils
  const userData = AuthenticationService.getUserData();
  const showAdvancedControls = ADVANCED_CONTROL_ROLES.includes(userData.role_type);
  const UserFilter = ({filterKey, filterFn, label}: {
    filterKey: keyof WorksitesFiltersType,
    filterFn?: (u: UsersMinimalSchema) => boolean,
    label?: string;
  }) => (
    // @ts-ignore
    <SearchSelect
      label={intl.formatMessage({id: label ?? filterKey})}
      loadOptions={
        (search: string, callback: (result: {label: string, value: string}[]) => void) => callback(
          users
            ?.filter((u) => !filterFn || filterFn(u))
            ?.filter((u) => !search || u.name.toLowerCase().includes(search.toLowerCase()))
            ?.map((u) => ({label: u.name, value: u.id.toString()}))
            ?.slice(0, 10)
            ?? []
        )
      }
      value={value[filterKey]}
      onChange={
        (value: {label: string, value: string}) => onChange(
          (prev) => ({...prev, [filterKey]: value})
        )
      }
      isDisabled={!users}
      isClearable
      defaultOptions
    />
  );

  return (
    <div className="flex flex-col gap-5 w-full">
      <h1 className="text-xl sm:text-2xl sm:truncate text-am-500">
        <TranslatedKey id="filters"/>
      </h1>
      <form className="flex w-full flex-col">
        {showAdvancedControls && <>
          {/* @ts-ignore */}
          <Toggle
            checked={value.include_inactive}
            onChange={() => onChange((prev) => ({...prev, include_inactive: !prev.include_inactive}))}
            label={intl.formatMessage({id: "worksite_includedeactivated_filter"})}
            marginCls=""
          />
          {/* @ts-ignore */}
          <Toggle
            checked={value.show_only_test}
            onChange={() => onChange((prev) => ({...prev, show_only_test: !prev.show_only_test}))}
            label={intl.formatMessage({id: "worksite_test_filter", defaultMessage: "Mostra solo cantieri di test"})}
            marginCls=""
          />
        </>}
        {/* @ts-ignore */}
        <Toggle
          checked={value.show_only_terminated}
          onChange={() => onChange((prev) => ({...prev, show_only_terminated: !prev.show_only_terminated}))}
          label={intl.formatMessage({id: "worksite_terminated_filter", defaultMessage: "Mostra solo cantieri terminati"})}
            marginCls=""
        />
        <div className="mt-2 pb-2">
          {/* @ts-ignore */}
          <Input
            value={value.search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange((prev) => ({...prev, search: e.target.value}))}
            label={intl.formatMessage({id: "worksite_generic_filter"})}
            hideBottomMargin
          />
        </div>
        {/* @ts-ignore */}
        <SearchSelect
          label={intl.formatMessage({id: "worksite_type"})}
          options={[
            ...(worksitesTypes || []).map((x) => ({
              value: x.cod_worksite_type,
              label: x.des_worksite_type,
            })),
          ]}
          value={value.type}
          onChange={(value: {label: string, value: string}) => onChange((prev) => ({...prev, type: value}))}
          isDisabled={!worksitesTypes}
          isClearable
        />
        {/* @ts-ignore */}
        <SearchSelect
          label={intl.formatMessage({id: "worksite_status"})}
          options={WORKSITE_STATUS_CHOICES.map((s) => ({label: intl.formatMessage({id: s}), value: s}))}
          value={value.status}
          onChange={(value: {label: string, value: string}) => onChange((prev) => ({...prev, status: value}))}
          isClearable
        />
        {/* @ts-ignore */}
        <SearchSelect
          label={intl.formatMessage({id: "worksite_province"})}
          options={provinces?.map((p) => ({label: p.des_province, value: p.cod_province})) || []}
          value={value.province}
          onChange={(value: {label: string, value: string}) => onChange((prev) => ({...prev, province: value}))}
          isDisabled={!provinces}
          isClearable
        />
        <UserFilter filterKey="creator" />
        <UserFilter filterKey="shared_with" />
        <UserFilter
          filterKey="visibile_to_manager"
          filterFn={(u) => u.role_name === "area_manager"}
          label="area_manager"
        />
        {!!value.visibile_to_manager && (
          <UserFilter
            filterKey="visibile_to_agent"
            filterFn={(u) =>
              u.role_name === "agent"
              && u.role_hierarchy.split(";").some((rhe) => rhe === `user_${value.visibile_to_manager!.value}`)
            }
            label="agent"
          />
        )}
      </form>
    </div>
  )
}